// contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiService } from '../services/api';
import { StorageService } from '../services/storage';
import { GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../services/firebase';

// Định nghĩa các role
export const ROLES = {
  ADMIN: 'admin',
  OWNER: 'owner',
  MANAGER: 'manager',
  STAFF: 'staff',
  RECEPTIONIST: 'receptionist',
  CUSTOMER: 'customer'
};

// Định nghĩa các permission
export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  MANAGE_BOOKINGS: 'manage_bookings',
  MANAGE_STAFF: 'manage_staff',
  MANAGE_EMPLOYEES: 'manage_staff',
  MANAGE_SERVICES: 'manage_services',
  MANAGE_CUSTOMERS: 'manage_customers',
  VIEW_REPORTS: 'view_reports'
};

// Ánh xạ role - permissions
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.MANAGE_EMPLOYEES,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.OWNER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.MANAGE_EMPLOYEES,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.STAFF]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_CUSTOMERS
  ],
  [ROLES.RECEPTIONIST]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_CUSTOMERS
  ],
  [ROLES.CUSTOMER]: [
    PERMISSIONS.VIEW_DASHBOARD
  ]
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to derive effective role/permissions from backend user data
  const normalizeUser = (backendUser) => {
    if (!backendUser) return null;

    let role = ROLES.CUSTOMER; // Default for users with no business
    let permissions = ROLE_PERMISSIONS[ROLES.CUSTOMER];

    // Check if user has businesses
    // Note: Backend might return businesses as array of objects
    if (backendUser.businesses && backendUser.businesses.length > 0) {
      // Try to find active context
      const currentBusinessId = localStorage.getItem('currentBusinessId');

      // Expand this if businesses are just IDs or objects
      const activeBusiness = backendUser.businesses.find(b =>
        (typeof b.businessId === 'string' ? b.businessId : b.businessId?._id || b.businessId) === currentBusinessId
      ) || backendUser.businesses[0];

      if (activeBusiness) {
        role = activeBusiness.role;
        // If backend provides specific permissions, use them, else defaults
        if (activeBusiness.permissions && activeBusiness.permissions.length > 0) {
          permissions = activeBusiness.permissions;
        } else {
          permissions = ROLE_PERMISSIONS[role] || [];
        }

        // Persist active business if not set or different
        if (!currentBusinessId) {
          const bid = typeof activeBusiness.businessId === 'string' ? activeBusiness.businessId : activeBusiness.businessId?._id;
          localStorage.setItem('currentBusinessId', bid);
        }
      }
    } else {
      // Explicitly ensure no Owner/Admin powers if no business
      role = ROLES.CUSTOMER;
      permissions = ROLE_PERMISSIONS[ROLES.CUSTOMER];
    }

    return { ...backendUser, role, permissions };
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      if (token) {
        const userData = await apiService.getCurrentUser();
        if (userData) {
          setUser(normalizeUser(userData));
        } else {
          // Token invalid
          localStorage.removeItem('authToken');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // Call Backend API
      const result = await apiService.login({ username, password });

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        // Reload user to get full profile including businesses
        await loadUser();
        return { success: true };
      }

      throw new Error('Đăng nhập thất bại: Không nhận được token');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Đăng nhập thất bại');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      // Call Backend API
      const result = await apiService.register(userData);

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        await loadUser();
        return { success: true };
      }

      // If register returns success but no token (e.g. verify email), logic might differ
      return { success: true };

    } catch (error) {
      console.error('Register error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLoginResult = async (result) => {
    try {
      const user = result.user;
      const token = await user.getIdToken();
      const apiResult = await apiService.socialLogin(token);

      if (apiResult.token) {
        localStorage.setItem('authToken', apiResult.token);
        await loadUser();
        return { success: true };
      }
      return { success: false, error: 'No token received' };
    } catch (e) {
      console.error("Social login handle error", e);
      return { success: false, error: e.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return await handleSocialLoginResult(result);
    } catch (error) {
      console.error("Google login error", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithFacebook = async () => {
    try {
      setLoading(true);
      const provider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return await handleSocialLoginResult(result);
    } catch (error) {
      console.error("Facebook login error", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setLoading(true);
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      return await handleSocialLoginResult(result);
    } catch (error) {
      console.error("Apple login error", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      StorageService.clearSession();
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    error,
    login,

    register,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    logout,
    hasPermission,
    hasRole,
    hasAnyRole,
    ROLES,
    PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);